import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, ExternalLink, Users, Zap, Shield, Clock, BarChart, Mail, Palette, Globe, Smartphone, CalendarCheck, ListChecks, Vote, Gift, Bell, UserCheck, LineChart } from 'lucide-react';
import { SiteHeader, SiteFooter } from '../../components/website/SharedLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TOOLS = [
  {
    slug: 'tireo',
    name: 'TIREO',
    tagline: 'Tirages au sort & sondages professionnels',
    url: 'https://tireo.app',
    color: 'from-violet-600 to-purple-700',
    accentColor: 'violet',
    description: 'Solution professionnelle pour organiser des tirages au sort et des sondages en direct avec transparence et sécurité.',
    longDescription: 'TIREO permet aux entreprises et associations d\'organiser des tirages au sort certifiés, des sondages interactifs et des votes en direct. Chaque tirage est horodaté, animé en temps réel sur une page live publique, et conforme RGPD.',
    targetAudience: [
      'Entreprises organisant des jeux concours',
      'Associations réalisant des tombolas ou loteries',
      'Organisateurs d\'événements en direct',
      'Marques cherchant à engager leur communauté'
    ],
    features: [
      { icon: Gift, title: 'Tirages au sort', desc: 'Tirage aléatoire avec animation spectaculaire, horodatage certifié et page live publique' },
      { icon: Vote, title: 'Sondages & Votes', desc: 'Sondages avec photos, vidéos YouTube et limite de votes par participant' },
      { icon: ListChecks, title: 'Gestion de listes', desc: 'Importez, gérez et exportez vos participants avec champs personnalisés illimités' },
      { icon: Mail, title: 'Export marketing', desc: 'Exportez vos contacts avec consentement RGPD pour vos campagnes email et SMS' },
      { icon: Palette, title: 'Personnalisation', desc: 'Personnalisez vos pages avec votre logo, couleurs et design unique' },
      { icon: Shield, title: 'RGPD & Conformité', desc: 'Consentement marketing intégré, demande de suppression et transparence totale' }
    ],
    benefits: [
      { stat: '100%', label: 'Transparent', desc: 'Tirages certifiés et horodatés' },
      { stat: 'Live', label: 'Temps réel', desc: 'Page publique avec animations' },
      { stat: 'RGPD', label: 'Conforme', desc: 'Protection des données garantie' },
      { stat: 'Pro', label: 'Clé en main', desc: 'Prêt à utiliser en 5 minutes' }
    ],
    howItWorks: [
      { step: 1, title: 'Créez votre événement', desc: 'Configurez votre tirage au sort ou sondage en quelques clics' },
      { step: 2, title: 'Importez vos participants', desc: 'Ajoutez-les manuellement ou importez un fichier CSV' },
      { step: 3, title: 'Lancez en direct', desc: 'Partagez la page live et lancez le tirage avec une animation spectaculaire' },
      { step: 4, title: 'Exploitez les résultats', desc: 'Exportez les gagnants et contacts pour vos campagnes marketing' }
    ]
  },
  {
    slug: 'resawork',
    name: 'ResaWork',
    tagline: 'Système de réservation professionnel',
    url: 'https://resawork.fr',
    color: 'from-emerald-600 to-teal-700',
    accentColor: 'emerald',
    description: 'Système de réservation qui s\'intègre à votre site web pour gérer vos rendez-vous et services efficacement.',
    longDescription: 'ResaWork est un système de réservation professionnel qui s\'intègre directement à votre site internet. Vos clients réservent en ligne 24h/24, reçoivent des rappels automatiques, et vous gérez votre planning et votre équipe depuis un tableau de bord centralisé.',
    targetAudience: [
      'Prestataires de services (jardinage, plomberie, électricité, ménage)',
      'Professionnels de la beauté et du bien-être',
      'Artisans et indépendants',
      'Toute entreprise proposant des rendez-vous ou prestations'
    ],
    features: [
      { icon: CalendarCheck, title: 'Réservation 24/7', desc: 'Vos clients réservent à tout moment via votre site web' },
      { icon: Bell, title: 'Rappels automatiques', desc: 'Rappels 72h et 24h avant le rendez-vous pour réduire les absences' },
      { icon: Smartphone, title: 'Widgets intégrables', desc: '3 types de widgets (demandes, abonnements, rendez-vous) pour votre site' },
      { icon: Users, title: 'Gestion d\'équipe', desc: 'Gérez les plannings et disponibilités de votre équipe' },
      { icon: LineChart, title: 'Statistiques en temps réel', desc: 'Suivez votre activité et votre chiffre d\'affaires en direct' },
      { icon: Globe, title: 'Compatible tout CMS', desc: 'WordPress, Wix, Shopify ou tout site via iframe ou script' }
    ],
    benefits: [
      { stat: '24/7', label: 'Disponible', desc: 'Réservation en ligne permanente' },
      { stat: '-80%', label: 'No-shows', desc: 'Grâce aux rappels automatiques' },
      { stat: '3', label: 'Widgets', desc: 'Adaptés à chaque besoin' },
      { stat: 'Live', label: 'Dashboard', desc: 'Stats et gestion en temps réel' }
    ],
    howItWorks: [
      { step: 1, title: 'Configurez vos services', desc: 'Définissez vos prestations, durées, tarifs et disponibilités' },
      { step: 2, title: 'Intégrez le widget', desc: 'Ajoutez le widget de réservation sur votre site en un copier-coller' },
      { step: 3, title: 'Recevez des réservations', desc: 'Vos clients réservent en ligne, vous recevez des notifications' },
      { step: 4, title: 'Gérez votre activité', desc: 'Planning, rappels automatiques, statistiques et facturation' }
    ]
  }
];

// ============================================
// TOOLS LISTING PAGE
// ============================================
const NosOutilsPage = () => {
  const [themeSettings, setThemeSettings] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/logos`);
        if (res.ok) setThemeSettings(await res.json());
      } catch (e) {}
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500 rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-violet-500 rounded-full blur-[150px]" />
        </div>
        <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-300 font-medium">Conçus et développés par MSC</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="tools-page-title">
            Nos Outils
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Des applications indépendantes conçues par notre équipe pour répondre à des besoins concrets. Chaque outil est le fruit de notre expertise technique et de notre passion pour l'innovation.
          </p>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="max-w-6xl mx-auto px-4 -mt-10 relative z-20 pb-24">
        <div className="grid md:grid-cols-2 gap-8">
          {TOOLS.map((tool) => (
            <Link
              key={tool.slug}
              to={`/site/nos-outils/${tool.slug}`}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-100 hover:-translate-y-1"
              data-testid={`tool-card-${tool.slug}`}
            >
              {/* Preview */}
              <div className={`relative h-52 bg-gradient-to-br ${tool.color} overflow-hidden`}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                    <span className="text-4xl font-black text-white tracking-tight">{tool.name}</span>
                  </div>
                </div>
                <div className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
                  <Globe className="w-3.5 h-3.5 text-white" />
                  <span className="text-xs text-white font-medium">{tool.url.replace('https://', '')}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-600 transition-colors">{tool.name}</h3>
                    <p className="text-sm text-slate-500 mt-0.5">{tool.tagline}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-cyan-500 group-hover:translate-x-1 transition-all mt-1" />
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{tool.description}</p>
                <div className="flex flex-wrap gap-2">
                  {tool.features.slice(0, 3).map((f, i) => (
                    <span key={i} className="px-2.5 py-1 bg-slate-50 text-slate-600 text-xs rounded-full border border-slate-100">
                      {f.title}
                    </span>
                  ))}
                  <span className="px-2.5 py-1 bg-slate-50 text-slate-400 text-xs rounded-full border border-slate-100">
                    +{tool.features.length - 3} autres
                  </span>
                </div>
              </div>
            </Link>
          ))}

          {/* Coming Soon Card */}
          <div className="relative bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 overflow-hidden flex items-center justify-center min-h-[380px]">
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-slate-400 mb-2">Prochainement</h3>
              <p className="text-sm text-slate-400">De nouveaux outils sont en cours de développement...</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Besoin d'un outil sur mesure ?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Notre équipe conçoit des applications adaptées à vos besoins métier. Discutons de votre projet.
          </p>
          <Link
            to="/site#contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
            data-testid="tools-cta-contact"
          >
            Parlons de votre projet
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export { TOOLS };
export default NosOutilsPage;
